import {
  DoubleSide,
  Material,
  MeshBasicMaterial,
  MeshLambertMaterial,
  MeshPhongMaterial,
  MeshPhysicalMaterial,
  MeshStandardMaterial,
  Texture,
} from "three";

type CompileHook = Material["onBeforeCompile"];

/**
 * Hardware instancing shares one program across a batch, so a material may only
 * join a batch while it still carries three's default compile hook.
 *
 * This used to also accept the screen-space specular-occlusion hook, which was
 * assigned as-is rather than wrapped for exactly this reason — a per-material
 * wrapper would have made every rivet ineligible. That pass has been removed,
 * so the default hook is the only one left that keeps a material instanceable.
 */
export function compileHookAllowsHardwareInstancing(
  hook: CompileHook,
): boolean {
  return hook === Material.prototype.onBeforeCompile;
}

function hasProperty<K extends PropertyKey>(
  material: Material,
  property: K,
): material is Material & Record<K, unknown> {
  return property in material;
}

const shininessToRoughness = (shininess: number): number => {
  if (!Number.isFinite(shininess) || shininess <= 0) return 0.5;
  return Math.min(1, Math.max(0.1, Math.sqrt(2 / (shininess + 2))));
};

const convertSingleMaterial = (original: Material): Material => {
  if (original instanceof MeshStandardMaterial) return original.clone();
  if (
    !(original instanceof MeshBasicMaterial) &&
    !(original instanceof MeshLambertMaterial) &&
    !(original instanceof MeshPhongMaterial)
  ) {
    return original.clone();
  }

  const modern = new MeshStandardMaterial();
  if (hasProperty(original, "color") && original.color) {
    modern.color.copy(original.color);
  }
  if (hasProperty(original, "map") && original.map) {
    modern.map = original.map as MeshStandardMaterial["map"];
  }
  if (hasProperty(original, "normalMap") && original.normalMap) {
    modern.normalMap = original.normalMap as MeshStandardMaterial["normalMap"];
  }
  modern.transparent = original.transparent;
  modern.opacity = original.opacity;
  modern.side = original.side;
  // Legacy diffuse maps are dielectric albedo, not a metallic workflow.
  modern.metalness = 0;
  modern.roughness = hasProperty(original, "shininess")
    ? shininessToRoughness(original.shininess)
    : 0.5;
  return modern;
};

/** Caller owns these fresh materials; retained for the piano-hinge renderer. */
export const convertToModernMaterial = (
  original: Material | Material[],
): Material | Material[] =>
  Array.isArray(original)
    ? original.map(convertSingleMaterial)
    : convertSingleMaterial(original);

export const setMaterialsDoubleSided = (
  material: Material | Material[] | null | undefined,
): void => {
  if (!material) return;
  for (const item of Array.isArray(material) ? material : [material]) {
    item.side = DoubleSide;
    item.needsUpdate = true;
  }
};

interface MaterialEntry {
  material: Material;
  references: number;
}

const sharedMaterials = new WeakMap<Material, Map<string, MaterialEntry>>();

/**
 * Identity, not appearance: two materials that differ only in these cannot
 * shade differently, so they must not split a batch.
 */
const KEY_EXCLUDED_PROPERTIES = new Set([
  "uuid",
  "name",
  "version",
  "userData",
  "_listeners",
]);

function mergeKeyRuns(
  keys: string[],
  scratch: string[],
  start: number,
  middle: number,
  end: number,
): void {
  let left = start;
  let right = middle;
  let next = start;
  // Run boundaries are within the dense Object.keys array (or its full copy).
  while (left < middle && right < end) {
    scratch[next++] =
      (keys[left] as string) <= (keys[right] as string)
        ? (keys[left++] as string)
        : (keys[right++] as string);
  }
  while (left < middle) scratch[next++] = keys[left++] as string;
  while (right < end) scratch[next++] = keys[right++] as string;
}

function materialKeyRuns(keys: string[]): number[] {
  const runs = [0];
  for (let i = 1; i < keys.length; i++) {
    if ((keys[i - 1] as string) > (keys[i] as string)) runs.push(i);
  }
  runs.push(keys.length);
  return runs;
}

/**
 * Sort only string keys in UTF-16 code-unit order, without per-comparison
 * callbacks on this per-mesh hot path. Natural runs also keep already ordered
 * material shapes cheap. O(n log n) work and O(n) temporary space; nothing is
 * cached, so mutations and custom property layouts need no invalidation.
 */
function sortMaterialKeys(keys: string[]): string[] {
  const runs = materialKeyRuns(keys);
  if (runs.length === 2) return keys;
  let scratch = new Array<string>(keys.length);
  while (runs.length > 2) {
    let nextRun = 1;
    for (let run = 0; run < runs.length - 1; run += 2) {
      const end = runs[Math.min(run + 2, runs.length - 1)] as number;
      mergeKeyRuns(
        keys,
        scratch,
        runs[run] as number,
        runs[run + 1] as number,
        end,
      );
      runs[nextRun++] = end;
    }
    runs.length = nextRun;
    const swap = keys;
    keys = scratch;
    scratch = swap;
  }
  return keys;
}

export function hardwareMaterialKey(material: Material): string {
  const properties: Record<string, unknown> = {};
  for (const key of sortMaterialKeys(Object.keys(material))) {
    if (KEY_EXCLUDED_PROPERTIES.has(key)) continue;
    const value = (material as unknown as Record<string, unknown>)[key];
    if (typeof value === "function") continue;
    if (value instanceof Texture) {
      properties[key] = value.uuid;
    } else if (key === "defines" && value && typeof value === "object") {
      // Kept ahead of the `toArray` branch, and a snapshot of own enumerable
      // keys: a `defines` object must not be treated as a vector, and inherited
      // keys must not reach the batch key. This used to also drop the
      // screen-space specular-occlusion define, which was added on the first
      // rendered frame — after early hardware had already been keyed — so the
      // key had to stay stable across it. That pass is gone; the ordering and
      // the snapshot are still load-bearing.
      properties[key] = { ...(value as Record<string, unknown>) };
    } else if (
      value &&
      typeof value === "object" &&
      "toArray" in value &&
      typeof value.toArray === "function"
    ) {
      properties[key] = value.toArray();
    } else {
      properties[key] = value;
    }
  }
  // Unlike Material.toJSON this neither encodes texture images nor quantizes
  // linear colours to 8-bit sRGB. Both are wrong for a cheap, exact batch key.
  return JSON.stringify(properties);
}

// Exact constructor matches only: a subclass may carry state this key cannot see.
const SHAREABLE_MATERIAL_TYPES = new Set<unknown>([
  MeshStandardMaterial,
  MeshPhysicalMaterial,
  MeshBasicMaterial,
  MeshLambertMaterial,
  MeshPhongMaterial,
]);

function canShareMaterial(source: Material): boolean {
  return (
    SHAREABLE_MATERIAL_TYPES.has(source.constructor) &&
    compileHookAllowsHardwareInstancing(source.onBeforeCompile) &&
    source.customProgramCacheKey === Material.prototype.customProgramCacheKey &&
    Object.keys(source.userData).length === 0 &&
    !source.clippingPlanes?.length
  );
}

/**
 * Source/cache materials and their textures are borrowed, never mutated or
 * disposed. Read-only resolved materials are shared until their last user leaves.
 */
export function acquireSupplyPartMaterial(
  source: Material,
  modernize = true,
): { material: Material; release: () => void } {
  const shareable = canShareMaterial(source);
  const createMaterial = () => {
    const material =
      modernize && shareable ? convertSingleMaterial(source) : source.clone();
    material.onBeforeCompile = source.onBeforeCompile;
    material.customProgramCacheKey = source.customProgramCacheKey;
    setMaterialsDoubleSided(material);
    return material;
  };
  if (!shareable) {
    const material = createMaterial();
    let released = false;
    return {
      material,
      release: () => {
        if (!released) material.dispose();
        released = true;
      },
    };
  }
  let variants = sharedMaterials.get(source);
  if (!variants) {
    variants = new Map();
    sharedMaterials.set(source, variants);
  }
  const key = `${source.version}:${modernize}:${hardwareMaterialKey(source)}`;
  let entry = variants.get(key);
  if (!entry) {
    const material = createMaterial();
    entry = { material, references: 0 };
    variants.set(key, entry);
  }
  entry.references++;
  const owned = entry;
  let released = false;
  return {
    material: owned.material,
    release: () => {
      if (released) return;
      released = true;
      if (--owned.references === 0) {
        variants.delete(key);
        owned.material.dispose();
      }
    },
  };
}
